import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Avatar,
    Box,
    Grid,
    Divider,
    Button,
    Paper,
    Select,
    Typography,
    usePagination,
    Container,
    ClickAwayListener,
    MenuItem,
    MenuList,
    TextField,
    Grow,
    Popper,
    FormGroup,
    InputAdornment,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Search from "@mui/icons-material/Search";
import { makeStyles } from "@mui/styles";
import * as Util from "../../../../services/global.service";
import cssVariables from "../../../../theme/cssVariables";
import { ReactComponent as ChevronExpandDown } from "../../../../assets/icons/chevron-expand-down.svg";
import { ReactComponent as ChevronExpandUp } from "../../../../assets/icons/chevron-expand-up.svg";
import { ReactComponent as FilterStatus } from "../../../../assets/icons/filter-status.svg";
import { ReactComponent as Nodata } from "../../../../assets/icons/nodata.svg";
import { ReactComponent as Manage } from "../../../../assets/images/manage.svg";
import { ReactComponent as Evaluate } from "../../../../assets/images/evaluate.svg";
import { ReactComponent as Result } from "../../../../assets/images/result.svg";
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Rating from '@mui/material/Rating';
import Stack from '@mui/material/Stack';
import StarIcon from '@mui/icons-material/Star';
import evaluation360Service from "../../../../services/360Evaluation.service";
import { listCycle } from "../../../../actions/admin";
import debounce from 'lodash.debounce';
import evaluateService from "../../../../services/evaluate.service";

const styles = {
    path: {
        color: "#68717A",
    },
    paPlanning: {
        marginTop: "-12px",
        marginLeft: "19px",
    },
    icon: {
        color: "#6B6B6B",
        marginBottom: "-3px",
        marginRight: "10px",
    },
    lastToolBar: {
        marginTop: "0px",
    },
    blueBadge: {
        background: "rgba(93, 161, 150, 0.15)",
        padding: "5px 12px",
        color: "#5DA196",
        border: "1px solid #5DA196",
        borderRadius: "4px"
    },
    link: {
        color: "#5DA196",
        cursor: "pointer",
    },
    end: {
        color: "#A3A3A3",
    },
    colorText: {
        color: "#404349",
        fontWeight: "400",
        paddingLeft: "10px"
    },
    box: {
        width: "calc(50% - 30px)",
        flexDirection: "row",
        margin: "15px 15px",
    },
    content: {
        background: "#FFFFFF",
        "box-shadow": "0px 0px 5px rgba(0, 0, 0, 0.1)",
        "border-radius": "10px",
        padding: "17px 22px",
        height: "100%",
    },
    emty: {
        display: "flex",
        "flex-flow": "column wrap",
        "background-color": "#fff",
        height: "220px",
        "box-shadow": "0px 0px 5px rgb(0 0 0 / 10%)",
        "border-radius": "10px",
        justifyContent: "center",
        "align-items": "center",
    },
    twoContent: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "row",
        "align-items": "center",
    },
    boxTwoContent: {
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        "align-items": "center",
    }
};

const useStyles = makeStyles({
    plusSvg: {
        "& > path": {
            fill: "#fff",
            stroke: "#fff",
        },
    },
    sortIcon: {
        "& > path": {
            fill: "#A0A0A0",
        },
    },
    draggingListItem: {
        background: 'rgb(235,235,235)'
    },
    toggleBtnRoot: {
        background: `transparent !important`,
        color: "#6B6B6B !important",
        width: "320px",
        border: "none !important",
    },
    toggleBtnSelected: {
        background: `${cssVariables.color.primary}!important`,
        color: "#fff!important",
    },
    flexPaper: {
        flex: 1,
        minWidth: 350,
        "box-shadow": "0px 2px 10px rgba(0, 0, 0, 0.1) !important",
        "border-radius": "10px !important        ",
        minHeight: "570px"
    },
    tab: {
        fontSize: "16px !important",
        fontWeight: "300 !important",
        // color: "#A3A3A3 !important",
        '&.Mui-selected': {
            color: cssVariables.color.primary + " !important"
        }
    },
    root: {
        display: 'flex',
        width: "100%",
        flexWrap: 'wrap',
        fontSize: "16px",
        lineHeight: "24px",
        fontWeight: "400",
    },
    iconContainer: {
        width: 40,
        height: 40,
        borderRadius: 5,
        marginTop: 3,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    profileContainer: {
        width: 40,
        height: 40,
        borderRadius: "50%",
        marginTop: 3,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    statusBadge: {
        width: "fit-content",
        minWidth: "120px",
        borderRadius: "5px",
        padding: "10px",
    },
    draftBg: {
        background: "#EFB76D26"
    },
    draftText: {
        color: "#EFB76D"
    },
    pendingBg: {
        background: "#F280261A"
    },
    pendingText: {
        color: "#F28026"
    },
    approveBg: {
        background: "#5DA19626"
    },
    approveText: {
        color: "#5DA196"
    },
    rejectedBg: {
        background: "#CC605826"
    },
    rejectedText: {
        color: "#CC6058"
    },
    notStartBg: {
        background: "#C4C4C41A"
    },
    notStartText: {
        color: "#C4C4C4",
    },
    cancelBg: {
        background: "#CC605826"
    },
    cancelText: {
        color: "#CC6058",
    },
    cancelPendingBg: {
        background: "#82828226"
    },
    cancelPendingText: {
        color: "#828282",
    },
    completedBg: {
        background: "rgba(93, 161, 150, 0.15);",
    },
    completedText: {
        color: "#5DA196",
    },
    blueBg: {
        background: "#6694CC",
    },
    purpleBg: {
        background: "#7580BD",
    },
    btnContainer: {
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        flexGrow: 1,
    },
    btnActive: {
        background: cssVariables.color.primary,
        color: `${cssVariables.color.white}!important`,
        "&:hover": {
            backgroundColor: "#4f8a80",
        },
    },
    btnInactive: {
        "&:hover": {
            backgroundColor: "#5da19626",
        },
    },
    btn: {
        transition: "0.2s",
        cursor: "pointer",
        borderRadius: "5px",
        padding: "10px",
        color: cssVariables.color.black,
    },
    paginationRoot: {
        color: `${cssVariables.color.primary}`,
        backgroundColor: "white!important",
        minWidth: "30px!important",
        padding: "5px!important",
    },
    paginationSelected: {
        color: "white!important",
        border: "1px solid rgba(93, 161, 150, 0.5)!important",
        backgroundColor: `${cssVariables.color.primary}!important`,
        minWidth: "30px!important",
        padding: "5px!important",
    },
    tableHover: {
        transition: "0.5s",
        "&:hover": {
            // background: "#4043490f",
            boxShadow: "3px 3px 5px rgba(0, 0, 0, 0.1)",
            cursor: "pointer",
        },
    },
    profileContainer: {
        width: 70,
        height: 70,
        borderRadius: "50%",
        marginTop: 3,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    select: {
        '& div ': {
            padding: "11px 14px"
        }
    },
});

function CustomPagination(props) {
    const { t } = useTranslation();
    const useStyles = makeStyles({
        paginationRoot: {
            color: `${cssVariables.color.primary}`,
            backgroundColor: "white!important",
            minWidth: "30px!important",
            padding: "5px!important",
        },
        paginationSelected: {
            color: "white!important",
            border: "1px solid rgba(93, 161, 150, 0.5)!important",
            backgroundColor: `${cssVariables.color.primary}!important`,
            minWidth: "30px!important",
            padding: "5px!important",
        },
    });
    const classes = useStyles();
    const List = styled("ul")({
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "flex",
    });
    const { items } = usePagination({
        count: props.count,
        showFirstButton: true,
        showLastButton: true,
    });
    return (
        <nav>
            <List>
                {items.length > 0 ? (
                    items.map(({ page, type, selected, ...item }, index) => {
                        let children = null;
                        selected = type === "page" && page === props.selected ? props.selected : selected
                        if (type === "start-ellipsis" || type === "end-ellipsis") {
                            children = "…";
                        } else if (type === "page") {
                            children = (
                                <Button
                                    {...item}
                                    size="small"
                                    type="button"
                                    style={{
                                        fontWeight: selected ? "bold" : undefined,
                                    }}
                                    variant="outlined"
                                    classes={{
                                        root: selected
                                            ? classes.paginationSelected
                                            : classes.paginationRoot,
                                    }}
                                    sx={{ margin: "0 5px" }}
                                >
                                    {page}
                                </Button>
                            );
                        } else if (type === "first" || type === "last") {
                            children = (
                                <Button
                                    {...item}
                                    disabled={type === "first" ? props.selected === 1 ? true : false : props.selected === props.count ? true : false}
                                    size="small"
                                    type="button"
                                    style={{
                                        fontWeight: selected ? "bold" : undefined,
                                    }}
                                    variant="outlined"
                                    classes={{
                                        root: selected
                                            ? classes.paginationSelected
                                            : classes.paginationRoot,
                                    }}
                                    sx={{ margin: "0 5px" }}
                                >
                                    {type === "first" ? <>&lt;&lt;</> : <>&gt;&gt;</>}
                                </Button>
                            );
                        } else {
                            children = (
                                <Button
                                    {...item}
                                    disabled={type === "previous" ? props.selected === 1 ? true : false : props.selected === props.count ? true : false}
                                    size="small"
                                    type="button"
                                    variant="outlined"
                                    classes={{
                                        root: classes.paginationRoot,
                                    }}
                                >
                                    {type === "previous"
                                        ? t("label.previous")
                                        : type === "next"
                                            ? t("label.next")
                                            : ""}
                                </Button>
                            );
                        }
                        return <li key={index}>{children}</li>;
                    })

                ) : (
                    <></>
                )}
                {props.setCurrentPage(items)}
            </List>
        </nav>
    );
}

const List360EvaliationEmployee = (props) => {
    const Ratings = styled(Rating)({
        '& .MuiRating-icon': {
            color: '#F7B254 !important',
        },
    });
    const { t, i18n } = useTranslation();
    const _ = require("lodash");
    const [list, setList] = React.useState([]);
    const [filteredList, setFilteredList] = React.useState([]);
    const [itemsProgress, setItemsProgress] = React.useState([]);
    const [mainItemsProgress, setMainItemsProgress] = React.useState([]);
    const classes = useStyles();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [open, setOpen] = React.useState(false);
    const [opens, setOpens] = React.useState(false);
    const [opensForm, setOpensForm] = React.useState(false);
    const [perPage, setPerPage] = React.useState(10);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalPage, setTotalPage] = React.useState(1);
    const [checkResired, setCheckRsquired] = React.useState({});
    const [reSet, setreSet] = React.useState(false);
    const anchorRef = React.useRef(null);
    const anchorRefs = React.useRef(null);
    const anchorRefsForm = React.useRef(null);
    const [completed, setCompleted] = React.useState(true);
    const [progress, setProgress] = React.useState(true);
    const [notStarted, setNotStarted] = React.useState(true);
    const [search, setSearchValue] = React.useState("");
    const [searchProgress, setSearchValueProgress] = React.useState("");
    const [type, setType] = React.useState([]);
    const [checktype, setCheckType] = React.useState({});
    const [form, setForm] = React.useState([]);
    const [checkform, setCheckForm] = React.useState({});
    const [status, setStatus] = React.useState([t('360Evaluation.label.assign'), t('360Evaluation.label.review'), t('360Evaluation.label.evaluation'), t('360Evaluation.label.signOff'), t('360Evaluation.label.completed')]);
    const [checkstatus, setCheckStatus] = React.useState({});
    const [competencyFormSelect, setCompetencyForm] = React.useState([]);
    const [competencyFormSelectValue, setCompetencyFormValue] = React.useState("");
    const [formStatus, setFormStatus] = React.useState("");
    const [orderBy, setOrderBy] = React.useState("");
    const [sort, setSort] = React.useState("");
    const handleChangeWorkloadForm = (event) => {
        setCompetencyFormValue(event.target.value);
    };
    const clickLink = (data) => {
        navigate(data, { replace: false });
    }
    useEffect(() => {
        handleGetList()
    }, []);
    const handleGetList = () => {
        evaluation360Service.getListEmployee(perPage, currentPage, formStatus, orderBy, sort).then(
            (response) => {
                setMainItemsProgress(response?.data?.list !== undefined ? response?.data?.list : []);
                setItemsProgress(response?.data?.list !== undefined ? response?.data?.list : []);
                setTotalPage(response?.data.total_page)
                setForm(response?.data?.filters?.form !== undefined ? response?.data?.filters?.form : [])
                // setStatus(response?.data?.filters?.status !== undefined ? response?.data?.filters?.status : [])
            },
        )
    };

    function handleListKeyDown(event) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }
    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };

    let checkIndeterminateStatus = () => {
        let check = true
        let data = checkstatus
        let getObjectKey = Object.keys(data)
        let checksTrue = getObjectKey.filter((rs) => data[rs] === true)
        let checksFalse = getObjectKey.filter((rs) => data[rs] === false)
        if (getObjectKey.length !== checksTrue.length && getObjectKey.length !== checksFalse.length) {
            check = true
        } else {
            check = false
        }
        return check;
    }
    useEffect(() => {
        let sumData = {}
        for (let index = 0; index <= status.length - 1; index++) {
            sumData[status[index]] = true
        }
        setCheckStatus(sumData)
    }, [status])
    const changeStatus = (statusSelect) => {
        let data = JSON.parse(JSON.stringify(checkstatus))
        let name = ""
        let sumData = {}
        if (statusSelect === "all") {
            let getObjectKey = Object.keys(data)
            let check = getObjectKey.filter((rs) => data[rs] === true)
            if (check.length !== status.length) {
                for (let index = 0; index <= status.length - 1; index++) {
                    sumData[status[index]] = true
                    if (sumData[status[index]] === true) {
                        if (status[index] === t('360Evaluation.label.assign')) {
                            name += name === "" ? "submit" : ",submit"
                        } else if (status[index] === t('360Evaluation.label.review')) {
                            name += name === "" ? "review" : ",review"
                        } else if (status[index] === t('360Evaluation.label.evaluation')) {
                            name += name === "" ? "evaluate" : ",evaluate"
                        } else if (status[index] === t('360Evaluation.label.signOff')) {
                            name += name === "" ? "signoff" : ",signoff"
                        } else if (status[index] === t('360Evaluation.label.completed')) {
                            name += name === "" ? "approved" : ",approved"
                        }
                    }
                }
            } else {
                for (let index = 0; index <= status.length - 1; index++) {
                    sumData[status[index]] = false
                }
            }
            setCheckStatus(sumData)
        } else {
            data[statusSelect] = !checkstatus[statusSelect]
            for (let index = 0; index <= status.length - 1; index++) {
                if (data[status[index]] === true) {
                    if (status[index] === t('360Evaluation.label.assign')) {
                        name += name === "" ? "submit" : ",submit"
                    } else if (status[index] === t('360Evaluation.label.review')) {
                        name += name === "" ? "review" : ",review"
                    } else if (status[index] === t('360Evaluation.label.evaluation')) {
                        name += name === "" ? "evaluate" : ",evaluate"
                    } else if (status[index] === t('360Evaluation.label.signOff')) {
                        name += name === "" ? "signoff" : ",signoff"
                    } else if (status[index] === t('360Evaluation.label.completed')) {
                        name += name === "" ? "approved" : ",approved"
                    }
                }
            }
            setCheckStatus(data)
        }
        setFormStatus(name)
        evaluation360Service.getListEmployee(perPage, currentPage, name !== "" ? name : "-", orderBy, sort).then(
            (response) => {
                setMainItemsProgress(response?.data?.list !== undefined ? response?.data?.list : []);
                setItemsProgress(response?.data?.list !== undefined ? response?.data?.list : []);
                setTotalPage(response?.data.total_page)
                // setStatus(response?.data?.filters?.status !== undefined ? response?.data?.filters?.status : [])
            },
        )
    };
    let checkAllStatus = () => {
        let check = true
        let data = checkstatus
        let getObjectKey = Object.keys(data)
        let checks = getObjectKey.filter((rs) => data[rs] === true)
        if (checks.length !== status.length) {
            check = false
        } else {
            check = true
        }
        return check;
    }
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleTotalPage = () => {
        const List = mainItemsProgress
        const totalPage =
            Math.ceil(List.length / perPage) === 0
                ? 1
                : Math.ceil(List.length / perPage);
        setTotalPage(totalPage);
    };
    useEffect(() => {
        handleTotalPage();
    }, [mainItemsProgress, perPage]);

    const handlePerPageChange = (event) => {
        setPerPage(event.target.value);
        evaluation360Service.getListEmployee(event.target.value, currentPage, formStatus, orderBy, sort).then(
            (response) => {
                setMainItemsProgress(response?.data?.list !== undefined ? response?.data?.list : []);
                setItemsProgress(response?.data?.list !== undefined ? response?.data?.list : []);
                setTotalPage(response?.data.total_page)
                setForm(response?.data?.filters?.form !== undefined ? response?.data?.filters?.form : [])
                // setStatus(response?.data?.filters?.status !== undefined ? response?.data?.filters?.status : [])
            },
        ).catch((err) => {

        })
    };
    const handleSetCurrentPage = (value) => {
        const getSelectedPage = value.filter(item => item.type === "page" && item.selected)
        if (getSelectedPage.length > 0) {
            setCurrentPage(getSelectedPage[0].page)
        }
    };
    const handleSetCurrentPageData = () => {
        handleGetList()
    };
    useEffect(() => {
        handleSetCurrentPageData()
    }, [currentPage]);
    const toTimestamp = (strDate) => {
        if (strDate !== "" && strDate !== null && strDate !== undefined) {
            const dt = Date.parse(strDate);
            return dt / 1000;
        } else {
            return 0;
        }
    }
    let sortData = (key, sort) => {
        setOrderBy(key)
        setSort(sort)
        // handleGetList()
        evaluation360Service.getListEmployee(perPage, currentPage, formStatus, key, sort).then(
            (response) => {
                setMainItemsProgress(response?.data?.list !== undefined ? response?.data?.list : []);
                setItemsProgress(response?.data?.list !== undefined ? response?.data?.list : []);
                setTotalPage(response?.data.total_page)
                setForm(response?.data?.filters?.form !== undefined ? response?.data?.filters?.form : [])
                // setStatus(response?.data?.filters?.status !== undefined ? response?.data?.filters?.status : [])
            },
        ).catch((err) => {

        })
    }

    const BootstrapTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} arrow classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.arrow}`]: {
            color: "#343A40",
        },
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#343A40",
            color: "#FFFFFF",
            padding: "11px 15px",
            fontSize: "14px"

        },
    }));

    const handleGoToDetail = (serial, isNew, type) => {
        if (isNew === true) {
            evaluateService.logIsRead(serial).then(
                (response) => {
                    if (type === "manage") {
                        navigate(`/kpi/360evaluation/manage/${serial}`, { replace: false });
                    } else if (type === "evaluate") {
                        navigate(`/kpi/360evaluation/${serial}/2`, { replace: false });
                    } else {
                        navigate(`/kpi/360evaluation/${serial}/result`, { replace: false });
                    }
                },
            )
        } else {
            if (type === "manage") {
                navigate(`/kpi/360evaluation/manage/${serial}`, { replace: false });
            } else if (type === "evaluate") {
                navigate(`/kpi/360evaluation/${serial}/2`, { replace: false });
            } else {
                navigate(`/kpi/360evaluation/${serial}/result`, { replace: false });
            }
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: "background.default",
                minHeight: "100%",
                py: 3,
            }}
        >
            <Container maxWidth="xl">
                <Grid container pt={2} pb={2}>
                    <Typography fontSize={16} lineHeight={1} color="#68717A"><span style={styles.link} onClick={() => clickLink('/kpi/home')}>{t('label.home')}</span> /  <span style={styles.end} >{t("sidebar.label.my360evaluation")}</span></Typography>
                </Grid>
                <Grid container spacing={2}>
                    <Grid container item >
                        <Grid item md={12} xs={12}>
                            <Box >
                                <Typography variant="h2" color="#404349" fontSize={36} lineHeight="40px">
                                    {t("sidebar.label.my360evaluation")}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid item xs>
                        <Grid container mt={2}>
                            <Grid container item >
                                <div className={classes.root}>
                                    <Paper className={classes.flexPaper} >
                                        <Box
                                            px="32px"
                                        >
                                            <Box
                                                px="25px"
                                                py="19px"
                                                sx={{ display: "flex", flexDirection: "row" }}
                                            >
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(30.52% - 24px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="500" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t("evaluation.employee")}
                                                    </Typography>
                                                    <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", "display": "flex" }}>
                                                        <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("name", "asc")} />
                                                        <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("name", "desc")} />
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(27.25% - 24px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="500" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {/* {t("evaluation.formName")} */}
                                                        {t("evaluation.formNameManage")}
                                                    </Typography>
                                                    <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", "display": "flex" }}>
                                                        <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("form_name", "asc")} />
                                                        <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("form_name", "desc")} />
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(13.78% - 24px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="500" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t('evaluation.dueDateManage')}
                                                        {/* {t('evaluation.dueDate')} */}
                                                    </Typography>
                                                    <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", "display": "flex" }}>
                                                        <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("due_date", "asc")} />
                                                        <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("due_date", "desc")} />
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(13.75% - 24px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="500" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t('evaluation.rating')}
                                                    </Typography>
                                                    <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", "display": "flex" }}>
                                                        <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("evaluator_rating", "asc")} />
                                                        <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("evaluator_rating", "desc")} />
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", flexDirection: "row", width: "calc(14.70% - 24px)" }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="500" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", "margin-right": "10px" }}>
                                                        {t('360Evaluation.label.formStatus')}
                                                    </Typography>
                                                    <Stack direction="row" spacing={2}>
                                                        <div>
                                                            <FilterStatus
                                                                ref={anchorRef}
                                                                id="composition-button"
                                                                style={{ cursor: "pointer" }}
                                                                onClick={handleToggle}
                                                            />
                                                            <Popper
                                                                open={open}
                                                                anchorEl={anchorRef.current}
                                                                role={undefined}
                                                                placement="bottom-start"
                                                                transition
                                                                disablePortal
                                                                style={{ "z-index": "2" }}
                                                            >
                                                                {({ TransitionProps, placement }) => (
                                                                    <Grow
                                                                        {...TransitionProps}
                                                                        style={{
                                                                            transformOrigin:
                                                                                placement === 'bottom-start' ? 'left top' : 'left bottom',
                                                                        }}
                                                                    >
                                                                        <Paper sx={{ "margin-top": "5px !important" }}>
                                                                            <ClickAwayListener onClickAway={handleClose}>
                                                                                <MenuList
                                                                                    autoFocusItem={open}
                                                                                    id="composition-menu"
                                                                                    aria-labelledby="composition-button"
                                                                                    onKeyDown={handleListKeyDown}
                                                                                >

                                                                                    <Box px="16px" py="6px">
                                                                                        {status !== undefined && status.length > 0 ?
                                                                                            <FormGroup>
                                                                                                <FormControlLabel control={<Checkbox indeterminate={checkIndeterminateStatus()} onChange={e => changeStatus("all")} checked={checkAllStatus()} />} label={t('competency.selectAll')} />
                                                                                                {status.map((rs) => (
                                                                                                    <FormControlLabel control={<Checkbox onChange={e => changeStatus(rs)} checked={checkstatus[rs]} />} label={rs} />
                                                                                                ))}
                                                                                            </FormGroup>
                                                                                            :
                                                                                            <MenuItem value="">
                                                                                                ---{t('label.notFund')}---
                                                                                            </MenuItem>
                                                                                        }
                                                                                    </Box>
                                                                                </MenuList>
                                                                            </ClickAwayListener>
                                                                        </Paper>
                                                                    </Grow>
                                                                )}
                                                            </Popper>
                                                        </div>
                                                    </Stack>
                                                </Box>
                                                <Box sx={{ display: "flex", "flex-direction": "row", width: "120px" }}>
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="500" sx={{ "text-align": "center", "white-space": "nowrap", width: "100%", overflow: "hidden", "text-overflow": "ellipsis" }}>
                                                        {t('label.actions')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Divider width="100%" />
                                        </Box>
                                        {Util.formatData(mainItemsProgress, []).length > 0 ? mainItemsProgress.map((row, index) => (
                                            <Box px="32px" >
                                                <Box sx={{ width: "100%", position: "relative", transition: "0.5s", }} key={row?.serial} >
                                                    <Box
                                                        sx={{ minHeight: "96px", background: "#FFF", width: "100%", display: "flex", "align-items": "center" }}
                                                        px="25px" py="20px"

                                                    >
                                                        <Box sx={{ width: "calc(30.52% - 24px)" }} pr="15px" >

                                                            <Box
                                                                display="flex"
                                                                sx={{
                                                                    flexFlow: "column",
                                                                    textAlign: "left",
                                                                    // marginLeft: 2,
                                                                    position: "relative",
                                                                }}
                                                            >
                                                                {row?.is_new ? (
                                                                    <div
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: "calc(50% - 5px)",
                                                                            left: "-25px",
                                                                            width: "10px",
                                                                            height: "10px",
                                                                            background: "#CC6058",
                                                                            borderRadius: "50%",
                                                                        }}
                                                                    ></div>
                                                                ) : (
                                                                    <></>
                                                                )}
                                                                <Box display="flex" alignItems="center">
                                                                    <Avatar
                                                                        src={Util.formatData(row?.employee?.photo, "")}
                                                                        sx={{
                                                                            cursor: "pointer",
                                                                            width: 40,
                                                                            height: 40,
                                                                        }}
                                                                    />
                                                                    <Box pl="10px" sx={{ maxWidth: "calc(100% - 40px)" }}>
                                                                        <Typography
                                                                            variant="h4"
                                                                            fontWeight={row?.is_new ? 500 : 400}
                                                                            fontSize={16}
                                                                            lineHeight="27px"
                                                                            color="#404349"
                                                                            sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                                                                        >
                                                                            {Util.formatData(row?.employee?.fullname, "")}
                                                                        </Typography>
                                                                        <Typography fontSize="16px" color="#AAAAAA" lineHeight="24px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                            {Util.formatData(row?.employee?.position, "")}
                                                                        </Typography>
                                                                        {Util.formatData(row?.employee?.organization, false) !== false ?
                                                                            <Typography fontSize="16px" color="#AAAAAA" lineHeight="24px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                                ({row?.employee?.organization})
                                                                            </Typography>
                                                                            :
                                                                            null
                                                                        }
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ width: "calc(27.25% - 24px)" }} pr="15px" >
                                                            <BootstrapTooltip title={Util.formatData(row?.form?.name, "")} placement="top">
                                                                <Typography
                                                                    variant="h4"
                                                                    fontWeight={row?.is_new ? 500 : 400}
                                                                    fontSize={16}
                                                                    lineHeight="27px"
                                                                    color="#404349"
                                                                    sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                                                                >
                                                                    {Util.formatData(row?.form?.name, "")}
                                                                </Typography>
                                                            </BootstrapTooltip>
                                                            <Typography fontSize="16px" color="#AAAAAA" lineHeight="24px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                {Util.dateMonthFormatAdmin(row?.form?.start_date)} - {Util.dateMonthFormatAdmin(row?.form?.end_date)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", flexDirection: "column", width: "calc(13.78% - 24px)" }} pr="15px">
                                                            <Typography fontWeight="400" fontSize={16} color="#404349" lineHeight="24px" sx={{ color: toTimestamp(new Date()) > toTimestamp(row?.due_date) && row?.status === "approved" ? "#CC6058" : "#404349", whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                {Util.dateDayFormatAdmin(row?.due_date, "")}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", flexDirection: "column", width: "calc(13.75% - 24px)" }} pr="15px">
                                                            {row?.evaluator_rating !== null && row?.evaluator_rating !== "" && row?.status === "approved" ?
                                                                <Box sx={{ display: "flex", 'align-items': "center", }}>
                                                                    <Ratings fontSize={19} readOnly name="text-feedback" emptyIcon={<StarIcon fontSize="inherit" />} defaultValue={Util.formatData(Number(row?.evaluator_rating).toFixed(1), 0)} max={1} />
                                                                    <Box>
                                                                        <Typography fontSize={16} pl="5px" pr="5px" fontWeight="400">
                                                                            {Util.formatData(Number(Util.formatData(row?.evaluator_rating, "0")).toFixed(row?.score_decimal), "0")}
                                                                        </Typography>
                                                                        {Util.formatData(row?.evaluator_rating_percent, false) !== false ?
                                                                            <Typography fontSize={16} pl="5px" pr="5px" fontWeight="400">
                                                                                ({Util.formatData(Number(Util.formatData(row?.evaluator_rating_percent, "0")).toFixed(row?.score_decimal), "0")}%)
                                                                            </Typography>
                                                                            : null}
                                                                    </Box>
                                                                </Box>
                                                                :
                                                                <Box sx={{ display: "flex", 'align-items': "center", }}>
                                                                    <Rating fontSize={19} readOnly name="text-feedback" emptyIcon={<StarIcon fontSize="inherit" />} defaultValue={Util.formatData(Number(row?.evaluator_rating).toFixed(1), 0)} max={1} />
                                                                    <Typography fontSize={18} pl="5px" pr="5px" fontWeight="400">
                                                                        {"-"}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                        </Box>
                                                        <Box sx={{ width: "calc(14.70% - 24px)" }}>
                                                            <Box py="8px" px="20px" sx={{ "max-width": "100%", width: "max-content", "border-radius": "5px", background: row?.status === "submit" ? "#EFB76D1A" : row?.status === "review" ? "#F28E2B1A" : row?.status === "evaluate" ? "#43B6DB1A" : row?.status === "signoff" ? "#FF71A01A" : row?.status === "approved" ? "#5DA1961A" : "none" }} >
                                                                <Typography fontSize={16} lineHeight="24px" fontWeight={500} color={row?.status === "submit" ? "#EFB76D" : row?.status === "review" ? "#F28E2B" : row?.status === "evaluate" ? "#43B6DB" : row?.status === "signoff" ? "#FF71A0" : row?.status === "approved" ? "#5DA196" : "#6B6B6B"} sx={{ "text-align": "center", "white-space": "nowrap", width: "100%", overflow: "hidden", "text-overflow": "ellipsis" }}>
                                                                    {row?.status === "submit" ? t('360Evaluation.label.assign') : row?.status === "review" ? t('360Evaluation.label.review') : row?.status === "evaluate" ? t('360Evaluation.label.evaluation') : row?.status === "signoff" ? t('360Evaluation.label.signOff') : row?.status === "approved" ? t('360Evaluation.label.completed') : t('label.draft')}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ display: "flex", "flex-direction": "row", width: "120px", "justify-content": "flex-end" }}>
                                                            <Box sx={{ display: "flex", "flex-direction": "row", width: "100px", "z-index": "1" }}>
                                                                {row?.action?.evaluate ?
                                                                    <Box pr="15px">
                                                                        <BootstrapTooltip title={t("360Evaluation.label.evaluate")} placement="top">
                                                                            <Evaluate style={{ cursor: "pointer" }} onClick={() => handleGoToDetail(row?.serial, row?.is_new, "evaluate")} />
                                                                        </BootstrapTooltip>
                                                                    </Box>
                                                                    :
                                                                    <Box pr="15px">
                                                                        <Evaluate style={{ opacity: "0.2", cursor: "no-drop", pointerEvents: "none" }} />
                                                                    </Box>
                                                                }
                                                                {row?.action?.manage ?
                                                                    <Box pr="15px">
                                                                        <BootstrapTooltip title={t("360Evaluation.label.manage")} placement="top">
                                                                            <Manage style={{ cursor: "pointer" }} onClick={() => handleGoToDetail(row?.serial, row?.is_new, "manage")} />
                                                                        </BootstrapTooltip>
                                                                    </Box>
                                                                    :
                                                                    <Box pr="15px">
                                                                        <Manage style={{ opacity: "0.2", cursor: "no-drop", pointerEvents: "none" }} />
                                                                    </Box>
                                                                }
                                                                {row?.action?.result ?
                                                                    <Box>
                                                                        <BootstrapTooltip title={t("360Evaluation.label.result")} placement="top">
                                                                            <Result style={{ cursor: "pointer" }} onClick={() => handleGoToDetail(row?.serial, row?.is_new, "result")} />
                                                                        </BootstrapTooltip>
                                                                    </Box>
                                                                    :
                                                                    <Box>
                                                                        <Result style={{ opacity: "0.2", cursor: "no-drop", pointerEvents: "none" }} />
                                                                    </Box>
                                                                }
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Divider width="100%" />
                                            </Box>
                                        ))
                                            :
                                            <Box sx={{ "padding-top": "50px", "text-align": "center", "padding-bottom": "500px" }}>
                                                <Nodata />
                                                <Typography color="#A0A0A0" fontWeight="500" fontSize={20} lineHeight="30px" >
                                                    {t('label.noDataFilter')}
                                                </Typography>
                                                <Typography color="#A0A0A0" fontWeight="400" fontSize={16} lineHeight="24px" >
                                                    {t('label.tryChangingFilter')}
                                                </Typography>
                                            </Box>
                                        }
                                    </Paper>
                                </div>
                            </Grid>
                            <Grid container mt={1} spacing={2} >
                                <Grid item xs sm md display="flex" alignItems="center">
                                    <Box mr={1}>
                                        <Typography variant="s14_fw400">{t('label.showMaxItems')}</Typography>
                                    </Box>
                                    <Select
                                        id="pagination"
                                        variant="outlined"
                                        defaultValue={10}
                                        value={perPage}
                                        onChange={handlePerPageChange}
                                        displayEmpty
                                        size="small"
                                        className="white-bg"
                                    >
                                        <MenuItem value={10}>
                                            <Typography style={{ fontWeight: "300" }}>10</Typography>
                                        </MenuItem>
                                        <MenuItem value={20}>
                                            <Typography style={{ fontWeight: "300" }}>20</Typography>
                                        </MenuItem>
                                        <MenuItem value={30}>
                                            <Typography style={{ fontWeight: "300" }}>30</Typography>
                                        </MenuItem>
                                    </Select>
                                </Grid>
                                <Grid item display="flex" alignItems="center" ml="auto">
                                    <Typography variant="s14_fw400">
                                        {t('label.page')} {currentPage} {t('label.of')} {totalPage}
                                    </Typography>
                                </Grid>
                                <Grid item ml="auto" display="flex" alignItems="center">
                                    <CustomPagination
                                        setCurrentPage={handleSetCurrentPage}
                                        selected={currentPage}
                                        count={totalPage}
                                    ></CustomPagination>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

            </Container>
        </Box>
    );
};
export default List360EvaliationEmployee;
